import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, HeartHandshake } from "lucide-react";
import { api, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";

export const rp = (n) => "Rp " + new Intl.NumberFormat("id-ID").format(n || 0);
export const pct = (c) => (c.target > 0 ? Math.min(100, Math.round(((c.collected || 0) / c.target) * 1000) / 10) : 0);
export const daysLeft = (d) => {
  if (!d) return null;
  const diff = Math.ceil((new Date(d) - new Date()) / 86400000);
  return diff >= 0 ? diff : 0;
};

export default function GalangDanaPage() {
  const { t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/campaigns").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="galang-dana-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
        <div className="flex items-center gap-3 mb-3">
          <HeartHandshake className="h-8 w-8 text-[#D4AF37]" />
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-emerald-950 tracking-tight">{t("galang.title")}</h1>
        </div>
        <p className="text-slate-600">{t("galang.subtitle")}</p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {items.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="group bg-white rounded-xl border border-emerald-900/10 shadow-sm overflow-hidden flex flex-col"
            data-testid={`campaign-card-${c.id}`}
          >
            <Link to={`/galang-dana/${c.id}`} className="relative aspect-[16/10] overflow-hidden bg-emerald-50 block">
              {c.image && <img src={imgUrl(c.image)} alt={c.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
              {c.category && (
                <span className="absolute top-3 left-3 text-[11px] font-bold uppercase tracking-wider text-white bg-emerald-900/85 rounded-full px-3 py-1">{c.category}</span>
              )}
            </Link>
            <div className="p-6 flex flex-col flex-1">
              <Link to={`/galang-dana/${c.id}`}>
                <h3 className="font-display text-xl font-bold text-emerald-950 leading-snug group-hover:text-emerald-700 transition-colors">{c.title}</h3>
              </Link>
              <div className="mt-4">
                <div className="h-2 rounded-full bg-emerald-100 overflow-hidden">
                  <div className="h-2 rounded-full bg-[#D4AF37]" style={{ width: `${pct(c)}%` }} />
                </div>
                <div className="flex justify-between items-baseline mt-2.5">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">{t("galang.terkumpul")}</p>
                    <p className="text-sm font-bold text-emerald-800">{rp(c.collected)}</p>
                  </div>
                  <p className="text-xs text-slate-500">{pct(c)}% {t("galang.tercapai")}</p>
                </div>
                {c.target > 0 && <p className="text-xs text-slate-400 mt-1">{t("galang.target")}: {rp(c.target)}</p>}
              </div>
              <Button asChild className="mt-5 rounded-full bg-emerald-800 hover:bg-emerald-900 w-full">
                <Link to={`/galang-dana/${c.id}`} data-testid={`campaign-donate-${c.id}`}>
                  {t("galang.donasi")} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
